import { useCallback, useEffect, useRef, useState } from 'react';
import { useSolanaLive } from './useSolanaLive';
import { fetchCurrentRoundTokenBalance } from '../solana/fetchers/currentRoundTokenBalance';

/**
 * Hook to detect when a game ends and track if user had tokens
 * Returns banner state and dismissal function
 */
export function useGameEndNotification(walletPublicKey) {
  const { currentGameState, currentGamePubkey } = useSolanaLive();
  const [notification, setNotification] = useState(null);
  const previousGameRef = useRef(null);
  const checkedGamesRef = useRef(new Set());

  const dismissNotification = useCallback(() => {
    setNotification(null);
  }, []);

  useEffect(() => {
    const checkForGameEnd = async () => {
      // Skip if no wallet connected
      if (!walletPublicKey) {
        return;
      }

      // Skip if no current game
      if (!currentGamePubkey || !currentGameState) {
        return;
      }

      // If we don't have a previous game yet, just store the current one
      if (!previousGameRef.current) {
        previousGameRef.current = {
          gamePubkey: currentGamePubkey,
          gameState: currentGameState,
        };
        return;
      }

      // Check if the game has changed
      const previousGame = previousGameRef.current;
      if (previousGame.gamePubkey === currentGamePubkey) {
        // Same game, no change
        return;
      }

      // Game has changed! Check if we already notified for this previous game
      if (checkedGamesRef.current.has(previousGame.gamePubkey)) {
        // Already notified for this game, update reference and continue
        previousGameRef.current = {
          gamePubkey: currentGamePubkey,
          gameState: currentGameState,
        };
        return;
      }

      // Mark this game as checked
      checkedGamesRef.current.add(previousGame.gamePubkey);

      // Check if user had tokens in the previous game
      try {
        const balanceResult = await fetchCurrentRoundTokenBalance(
          walletPublicKey,
          previousGame.gameState
        );

        const balance = parseFloat(balanceResult.displayAmount || '0');

        if (balance > 0) {
          // User had tokens! Show notification
          setNotification({
            gameId: previousGame.gameState.game_id || 'Unknown Game Number',
            gamePubkey: previousGame.gamePubkey,
            mint: previousGame.gameState.mint,
            balance: balanceResult.displayAmount,
          });
        }
      } catch (error) {
        console.error('Error checking previous game balance:', error);
      }

      // Update the previous game reference
      previousGameRef.current = {
        gamePubkey: currentGamePubkey,
        gameState: currentGameState,
      };
    };

    checkForGameEnd();
  }, [currentGamePubkey, currentGameState, walletPublicKey]);

  // Clear notification when wallet disconnects
  useEffect(() => {
    if (!walletPublicKey) {
      setNotification(null);
      previousGameRef.current = null;
      checkedGamesRef.current.clear();
    }
  }, [walletPublicKey]);

  return {
    notification,
    dismissNotification,
  };
}
